<template>
  <div class="min-h-screen bg-base-200">
    <header v-if="!isLoginPage && !isFullViewport" class="bg-base-100 shadow-sm border-b border-base-300">
      <div class="w-full max-w-screen-2xl mx-auto px-6 py-2">
        <div class="flex items-center justify-between">
          <nav class="flex gap-2">
            <router-link to="/sessions" class="btn btn-ghost btn-sm" :class="{ 'btn-active': route.path === '/sessions' }">
              Sessions
            </router-link>
            <button
              type="button"
              class="btn btn-ghost btn-sm"
              :class="{ 'btn-active': showAssistant || route.path === '/assistant' }"
              :disabled="assistantLoading"
              @click="openAssistantModal"
              data-testid="nav-assistant"
            >
              <span v-if="assistantLoading" class="loading loading-spinner loading-xs mr-1"></span>
              Assistant
            </button>
            <router-link to="/analytics" class="btn btn-ghost btn-sm" :class="{ 'btn-active': route.path === '/analytics' }">
              Analytics
            </router-link>
            <router-link to="/projects" class="btn btn-ghost btn-sm" :class="{ 'btn-active': route.path === '/projects' }">
              Projects
            </router-link>
            <router-link to="/admin" class="btn btn-ghost btn-sm" :class="{ 'btn-active': route.path === '/admin' }">
              Admin
            </router-link>
          </nav>
          <div class="flex items-center gap-2">
            <span v-if="authStore.user" class="text-sm text-base-content/60 hidden sm:inline">
              {{ authStore.user.username }}
            </span>
            <button v-if="authStore.isAuthenticated" @click="handleLogout" class="btn btn-ghost btn-sm">
              Sign out
            </button>
            <ThemeSwitcher />
          </div>
        </div>
      </div>
    </header>

    <!-- Single <main> + single <router-view> regardless of layout mode.
         The wrapper styling switches via class/style bindings; the
         router-view stays MOUNTED across route changes so the KeepAlive
         cache survives a /sessions → /replay/:id → /sessions round-trip.
         Splitting this into two v-if branches earlier destroyed the
         KeepAlive instance every time the route mode flipped, which
         meant SessionList was re-fetched + reset on every return. -->
    <main :class="mainClass" :style="mainStyle">
      <h1 class="sr-only">Tracium</h1>
      <div :class="contentClass">
        <router-view v-slot="{ Component }">
          <KeepAlive :include="KEEP_ALIVE">
            <component :is="Component" />
          </KeepAlive>
        </router-view>
      </div>
    </main>

    <!-- Assistant modal launched from the nav — same dialog skeleton the
         session-view uses (see SessionReplay.vue's ask-modal) so the two
         entry points feel identical. No sessionId passed → cross-session
         scope (RQL + APM only, no devtools/timeline tools). -->
    <dialog :open="showAssistant" class="modal" data-testid="nav-assistant-modal">
      <div class="modal-box max-w-3xl p-0 overflow-hidden" style="height: 80vh; max-height: 720px;">
        <div class="absolute top-2 right-2 z-10">
          <button class="btn btn-sm btn-ghost btn-square" @click="showAssistant = false" title="Close">✕</button>
        </div>
        <div v-if="assistantError" class="p-4">
          <div class="alert alert-error">{{ assistantError }}</div>
        </div>
        <AssistantChat
          v-else-if="showAssistant && assistantProjectId"
          :project-id="assistantProjectId"
          :embedded="true"
        />
      </div>
      <div class="modal-backdrop bg-black/50" @click="showAssistant = false"></div>
    </dialog>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import ThemeSwitcher from './components/ThemeSwitcher.vue'
import AssistantChat from './components/AssistantChat.vue'
import { useAuthStore } from './stores/authStore'
import { apiFetch } from '@/utils/api'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

const isLoginPage = computed(() => !!route.meta.isLoginPage || !authStore.isAuthenticated)

// Routes that opt-in to 'modal' mode: full viewport, no app header, no
// max-width/padding wrappers — currently just /replay/:id so the player
// + side panels get every available pixel. New routes can flip the
// meta.fullViewport flag to join.
const isFullViewport = computed(() => !isLoginPage.value && !!route.meta.fullViewport)

// Component-name allowlist for <KeepAlive>. Caching a component means its
// state (refs, filters, fetched data, scroll position) survives
// navigation. Currently just SessionList so returning from /replay/:id
// restores the page exactly where it was. Heavy or session-specific
// components (SessionReplay, AssistantPage) are intentionally OUT of
// this list — caching them would pin large per-session payloads in
// memory across navigations.
const KEEP_ALIVE = ['SessionList']

// Wrapper styling computed off route meta. Three modes:
//   - login:        bare (no app shell)
//   - fullViewport: edge-to-edge 100vh with hidden overflow (replay route)
//   - normal:       centered card with max-width + padding
const mainClass = computed(() => {
  if (isLoginPage.value) return ''
  if (isFullViewport.value) return 'w-full'
  return 'w-full max-w-450 mx-auto px-6 py-4'
})
const mainStyle = computed(() =>
  isFullViewport.value ? 'height: 100vh; overflow: hidden;' : '')
const contentClass = computed(() => {
  if (isLoginPage.value || isFullViewport.value) return ''
  return 'card-modern w-full py-4'
})

const handleLogout = async () => {
  await authStore.logout()
  router.push('/login')
}

// Assistant modal — main-menu entry no longer navigates to /assistant; it
// opens AssistantChat in a modal mirroring the in-session experience.
// The /assistant route still works for bookmarks/deep links (it renders
// AssistantPage as before).
const showAssistant = ref(false)
const assistantProjectId = ref<string>('')
const assistantError = ref<string | null>(null)
const assistantLoading = ref(false)

async function openAssistantModal() {
  assistantError.value = null
  if (!assistantProjectId.value) {
    assistantLoading.value = true
    try {
      const resp = await apiFetch('/projects')
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
      const data = await resp.json()
      if (!Array.isArray(data) || data.length === 0) {
        throw new Error('No projects available. Create one on the Projects page first.')
      }
      // /api/projects returns "projects/<id>"; the assistant endpoint URL
      // expects just the slug (controller re-adds the prefix).
      const id = data[0].id as string
      assistantProjectId.value = id.startsWith('projects/') ? id.slice('projects/'.length) : id
    } catch (e) {
      assistantError.value = e instanceof Error ? e.message : String(e)
    } finally {
      assistantLoading.value = false
    }
  }
  showAssistant.value = true
}
</script>
